import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
export type ViewType = 'whatsapp' | 'templates' | 'send-templates' | 'campaign-history' | 'phonebook' | 'analytics' | 'agent-triggers' | 'agent-reports' | 'dashboard' | 'ai-persona' | 'help' | 'test-chat' | 'media' | 'team' | 'team-access' | 'addons' | 'addon-zoho_crm' | 'addons/zoho-crm' | 'addons/google-calendar' | 'addons/leads' | 'addons/crm-clients' | 'data' | 'intelligence';

interface NavigationState {
  activeView: ViewType;
  isNavigationCollapsed: boolean;
  isTransitioning: boolean;
  previousView: ViewType | null;
}

interface NavigationHistory {
  view: ViewType;
  timestamp: number;
}

export const useNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [navigationState, setNavigationState] = useState<NavigationState>({
    activeView: 'whatsapp',
    isNavigationCollapsed: false,
    isTransitioning: false,
    previousView: null,
  });

  const [navigationHistory, setNavigationHistory] = useState<NavigationHistory[]>([]);

  // Valid view paths
const validViews: ViewType[] = ['whatsapp', 'templates', 'send-templates', 'campaign-history', 'phonebook', 'analytics', 'agent-triggers', 'agent-reports', 'dashboard', 'ai-persona', 'help', 'test-chat', 'media', 'team', 'team-access', 'addons', 'addon-zoho_crm', 'addons/zoho-crm', 'addons/google-calendar', 'addons/leads', 'addons/crm-clients', 'data', 'intelligence'];
  // Update active view based on current route
  useEffect(() => {
    const path = location.pathname.slice(1) || 'whatsapp';
    
    if (validViews.includes(path as ViewType)) {
      const newView = path as ViewType;
      
      setNavigationState(prev => {
        // Only update if the view actually changed
        if (prev.activeView !== newView) {
          return {
            ...prev,
            previousView: prev.activeView,
            activeView: newView,
            isTransitioning: true,
          };
        }
        return prev;
      });

      // Add to navigation history
      setNavigationHistory(prev => {
        const newEntry: NavigationHistory = {
          view: newView,
          timestamp: Date.now(),
        };
        
        // Keep only last 10 entries
        const updatedHistory = [newEntry, ...prev.slice(0, 9)];
        return updatedHistory;
      });

      // Clear transition state after animation
      const timer = setTimeout(() => {
        setNavigationState(prev => ({
          ...prev,
          isTransitioning: false,
        }));
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Handle view changes with smooth transitions
  const handleViewChange = useCallback((view: ViewType) => {
    if (view === navigationState.activeView) return;

    // Set transitioning state
    setNavigationState(prev => ({
      ...prev,
      isTransitioning: true,
    }));

    // Navigate to new view
    navigate(`/${view}`);
  }, [navigate, navigationState.activeView]);

  // Toggle navigation panel
  const toggleNavigation = useCallback(() => {
    setNavigationState(prev => ({
      ...prev,
      isNavigationCollapsed: !prev.isNavigationCollapsed,
    }));
  }, []);

  // Go back to previous view
  const goToPreviousView = useCallback(() => {
    if (navigationHistory.length > 1) {
      const previousEntry = navigationHistory[1];
      handleViewChange(previousEntry.view);
    }
  }, [navigationHistory, handleViewChange]);

  // Check if we can go back
  const canGoBack = navigationHistory.length > 1;

  // Get view title and description
  const getViewInfo = useCallback((view: ViewType) => {
    const viewInfo = {
      whatsapp: {
        title: 'WhatsApp Agent',
        description: 'Manage WhatsApp conversations and messaging',
        icon: '💬',
      },
      templates: {
        title: 'Message Templates',
        description: 'Create and manage message templates',
        icon: '📝',
      },
      'send-templates': {
        title: 'Send Templated Messages',
        description: 'Send templated messages to contacts',
        icon: '📤',
      },
      phonebook: {
        title: 'Phonebook',
        description: 'Manage contacts and categories',
        icon: '📞',
      },
      analytics: {
        title: 'Analytics',
        description: 'View analytics and reporting data',
        icon: '📊',
      },
      'agent-triggers': {
        title: 'Agent Triggers',
        description: 'Access agent-detected AI triggers and monitoring',
        icon: '📋',
      },
      'agent-reports': {
        title: 'Agent Reports',
        description: 'Agent-focused AI analysis and operational insights',
        icon: '🤖',
      },
      dashboard: {
        title: 'Dashboard',
        description: 'Main dashboard overview',
        icon: '🏠',
      },
      'ai-persona': {
        title: 'AI Persona',
        description: 'Create and manage AI triggers and personas for the chatbot',
        icon: '🤖',
      },
      help: {
        title: 'Help & Support',
        description: 'Get help and support',
        icon: '❓',
      },
      'test-chat': {
        title: 'Test Chat',
        description: 'Test your AI chatbot with different scenarios',
        icon: '',
      },
      media: {
        title: 'Media Library',
        description: 'Upload and manage media files',
        icon: '📁',
      },
      team: {
        title: 'Team Management',
        description: 'Manage team members for handoff notifications',
        icon: '👥',
      },
      'team-access': {
        title: 'Team Access',
        description: 'Manage workspace members and permissions',
        icon: '👥',
      },
      addons: {
        title: 'Add-on Services',
        description: 'Manage third-party integrations and services',
        icon: '🔧',
      },
      'addon-zoho_crm': {
        title: 'Zoho CRM',
        description: 'Manage leads and customer relationships',
        icon: '📊',
      },
      'addons/zoho-crm': {
        title: 'Zoho CRM',
        description: 'Manage leads and customer relationships',
        icon: '📊',
      },
      'addons/google-calendar': {
        title: 'Google Calendar',
        description: 'Manage appointments and calendar events',
        icon: '📅',
      },
      'addons/leads': {
        title: 'Leads',
        description: 'Capture and manage leads from WhatsApp conversations',
        icon: '🎯',
      },
      'addons/crm-clients': {
        title: 'Clients',
        description: 'Manage your active client relationships',
        icon: '🤝',
      },
      data: {
        title: 'Business Data',
        description: 'Manage your FAQs, services, and business information',
        icon: '📁',
      },
      intelligence: {
        title: 'Intelligence',
        description: 'Query your business knowledge base using AI',
        icon: '💡',
      },
    };
  
    return viewInfo[view];
  }, []);

  return {
    navigationState,
    navigationHistory,
    handleViewChange,
    toggleNavigation,
    goToPreviousView,
    canGoBack,
    getViewInfo,
    validViews,
  };
};

export default useNavigation;